import type React from "react";
import { useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "~/images/close.svg?react";
import InfoIcon from "~/images/help_black.svg?react";
import styles from "./index.module.scss";

interface KnowledgeInfoTooltipProps {
  className?: string;
}

const KnowledgeInfoTooltip: React.FC<KnowledgeInfoTooltipProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleTooltip = () => {
    setIsOpen(!isOpen);
  };

  const closeTooltip = () => {
    setIsOpen(false);
  };

  return (
    <div className={`${styles["tooltipContainer"]} ${className || ""}`}>
      <button
        className={styles["infoButton"]}
        onClick={toggleTooltip}
        aria-label="ナレッジ機能の説明を表示"
      >
        <InfoIcon />
      </button>

      {isOpen &&
        createPortal(
          <>
            <div className={styles["overlay"]} onClick={closeTooltip} />
            <div
              className={styles["tooltip"]}
              role="dialog"
              aria-modal="true"
              aria-label="ナレッジ機能について"
            >
              <div className={styles["header"]}>
                <h3 className={styles["title"]}>ナレッジ機能について</h3>
                <button
                  className={styles["closeButton"]}
                  onClick={closeTooltip}
                  aria-label="ツールチップを閉じる"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className={styles["content"]}>
                <section className={styles["section"]}>
                  <h4 className={styles["sectionTitle"]}>概要</h4>
                  <p className={styles["description"]}>
                    「ナレッジ」は、情報の欠如を防ぐことを目的とした知識データベースで、文字起こしや議事録など、ユーザーが入力した情報を基にAIが自動的に生成します。手動での追加も可能です。
                  </p>
                </section>

                <section className={styles["section"]}>
                  <h4 className={styles["sectionTitle"]}>主な機能と役割</h4>
                  <div className={styles["featureList"]}>
                    <div className={styles["feature"]}>
                      <div>
                        <strong>ナレッジの蓄積</strong>
                        <p>AIとの対話で得られた重要な知見を「ナレッジ」として保存できます。</p>
                      </div>
                    </div>

                    <div className={styles["feature"]}>
                      <div>
                        <strong>AIの学習向上</strong>
                        <p>
                          保存されたナレッジをAIが活用することで、AIの思考とユーザーの思考が近づき、生成物の精度が向上します。
                        </p>
                      </div>
                    </div>

                    <div className={styles["feature"]}>
                      <div>
                        <strong>コンテキストの付与</strong>
                        <p>ナレッジは、AIの応答生成において前提条件として活用されます。</p>
                      </div>
                    </div>

                    <div className={styles["feature"]}>
                      <div>
                        <strong>プロンプトのカスタマイズ</strong>
                        <p>ユーザーがナレッジをアタッチしてプロンプトのカスタマイズが可能です。</p>
                      </div>
                    </div>
                  </div>
                </section>

                <section className={styles["section"]}>
                  <h4 className={styles["sectionTitle"]}>利用例</h4>
                  <div className={styles["example"]}>
                    <p>
                      <strong>例1: 機能要件書の精度向上</strong>
                    </p>
                    <p>
                      プロジェクトで使用する特定の業界用語や略語をナレッジとして登録することで、AIがそれらを理解し、より正確な機能要件書を生成できます。
                    </p>
                  </div>
                  <div className={styles["example"]}>
                    <p>
                      <strong>例2: 設計パターンの共有</strong>
                    </p>
                    <p>
                      プロジェクト固有の設計パターンやコーディング規約をナレッジに保存することで、一貫性のある設計書やコードを生成できます。
                    </p>
                  </div>
                </section>

                <div className={styles["note"]}>
                  <p>
                    <strong>注意:</strong>{" "}
                    ナレッジが蓄積されすぎると不要な情報が混入するリスクがあるため、定期的な見直しと整理が重要です。
                  </p>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

export default KnowledgeInfoTooltip;
